// src/pages/WordMatchGameV2.js
import React, { useEffect, useState, useCallback } from 'react';
import confetti from 'canvas-confetti';
import {
  DndContext,
  useDraggable,
  useDroppable,
  TouchSensor,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Home, RotateCcw, Trophy, Star, Shuffle, Target } from 'lucide-react';
import useAudio from '../../hooks/useAudio';
import useFirebase from '../../hooks/useFirebase';
import { Link } from 'react-router-dom';
import useAnalytics from '../../hooks/useAnalytics';

const WordMatchGameV2 = () => {
  // Initialize hooks
  const { saveSessionData } = useAnalytics();
  const { 
    userId,
    level,
    setLevel,
    maxLevel,
    levelsData,
    bestScores,
    isLoading,
    saveLevelData,
    updateLevel,
    unlockNextLevel,
    resetAllProgress
  } = useFirebase('wordMatchGame');

  const {
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    playWhooshSound,
    playStarSound,
    startBackgroundMusic,
    AudioElements
  } = useAudio();

  // Game state
  const [gamePhase, setGamePhase] = useState('menu'); // 'menu', 'playing', 'complete'
  const [currentLevelWords, setCurrentLevelWords] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [currentSetMatches, setCurrentSetMatches] = useState([]);

  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);
  
  // Enhanced scoring system
  const [totalScore, setTotalScore] = useState(0);
  const [timeBonus, setTimeBonus] = useState(0);
  const [streakBonus, setStreakBonus] = useState(0);
  const [accuracyBonus, setAccuracyBonus] = useState(0);
  const [stars, setStars] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [correctAttempts, setCorrectAttempts] = useState(0);
  const [levelStartTime, setLevelStartTime] = useState(Date.now());
  const [showScoringHelp, setShowScoringHelp] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [levelCompleteData, setLevelCompleteData] = useState(null);
  
  const [shuffledWords, setShuffledWords] = useState([]);
  const [currentDraggables, setCurrentDraggables] = useState([]);
  const [currentTargets, setCurrentTargets] = useState([]);
  const [feedback, setFeedback] = useState({});
  const [matchedTargets, setMatchedTargets] = useState({});
  const [wordIndex, setWordIndex] = useState(0);
  const [celebrations, setCelebrations] = useState({});
  const [sparkles, setSparkles] = useState({});
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [currentAttemptScore, setCurrentAttemptScore] = useState(0);
  const wordsPerPage = 5;

  // Modal state
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  // Sensor configuration for touch support
  const mouseSensor = useSensor(MouseSensor, {
  // Require the mouse to move by 10 pixels before activating
  activationConstraint: {
    distance: 10,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
  // Press delay of 250ms, with tolerance of 5px of movement
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  // Scoring constants
  const BASE_POINTS = 10;
  const STREAK_MULTIPLIER = 2;
  const MAX_TIME_BONUS = 50;
  const MAX_ACCURACY_BONUS = 100;
  const PERFECT_ACCURACY_BONUS = 150;

  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  const updateCurrentSets = useCallback((words, index) => {
    const currentSet = words.slice(index, index + wordsPerPage);
    setCurrentDraggables(shuffleArray([...currentSet]));
    setCurrentTargets(shuffleArray([...currentSet]));
    
    setCurrentSetMatches([]);
    setMatchedTargets({});
    setCelebrations({});
    setSparkles({});
  }, [shuffleArray, wordsPerPage]);

  const calculateStars = useCallback((score, totalWords, accuracy) => {
    const maxPossibleScore = totalWords * BASE_POINTS + MAX_TIME_BONUS + MAX_ACCURACY_BONUS;
    const scorePercentage = (score / maxPossibleScore) * 100;
    
    if (accuracy === 100 && scorePercentage >= 90) return 3;
    if (accuracy >= 85 && scorePercentage >= 70) return 2;
    if (accuracy >= 60 && scorePercentage >= 50) return 1;
    return 0;
  }, []);

  const calculateTimeBonus = useCallback((startTime, totalWords) => {
    const timeSpent = (Date.now() - startTime) / 1000;
    const idealTime = totalWords * 5;
    const bonus = Math.max(0, Math.min(MAX_TIME_BONUS, idealTime - timeSpent + 20));
    return Math.round(bonus);
  }, []);

  const calculateAccuracyBonus = useCallback((correct, total) => {
    const accuracy = (correct / total) * 100;
    if (accuracy === 100) return PERFECT_ACCURACY_BONUS;
    return Math.round((accuracy / 100) * MAX_ACCURACY_BONUS);
  }, []);

  const resetLevelScoring = useCallback(() => {
    setTotalScore(0);
    setTimeBonus(0);
    setStreakBonus(0);
    setAccuracyBonus(0);
    setStars(0);
    setAttempts(0);
    setCorrectAttempts(0);
    setConsecutiveCorrect(0);
    setLevelStartTime(Date.now());
    setCurrentAttemptScore(0);
  }, []);

  const resetLevelState = useCallback(() => {
    setAllMatches([]);
    setCurrentSetMatches([]);
    setMatchedTargets({});
    setWordIndex(0);
    setFeedback({});
    setCelebrations({});
    setSparkles({});
    resetLevelScoring();
  }, [resetLevelScoring]);

  const triggerCelebration = useCallback((matchId, isStreak = false, earnedStars = 0) => {
    setCelebrations(prev => ({ ...prev, [matchId]: true }));
    setSparkles(prev => ({ ...prev, [matchId]: true }));
    
    let colors = ['#00ff00', '#32cd32', '#90EE90'];
    let particleCount = 100;
    let shapes = ['circle'];
    
    if (earnedStars > 0) {
      colors = ['#FFD700', '#FFA500', '#FF69B4'];
      shapes = ['star'];
      particleCount = 150 + (earnedStars * 50);
      playStarSound();
    } else if (isStreak) {
      colors = ['#FFD700', '#FFA500', '#FF6347'];
      shapes = ['star', 'circle'];
      particleCount = 150;
      playStreakSound();
    } else {
      playCorrectSound();
    }
    
    confetti({
      particleCount,
      spread: isStreak || earnedStars > 0 ? 120 : 80,
      origin: { y: 0.6 },
      colors,
      shapes,
      gravity: 0.8,
      drift: 0.1
    });

    setTimeout(() => {
      setCelebrations(prev => ({ ...prev, [matchId]: false }));
      setSparkles(prev => ({ ...prev, [matchId]: false }));
    }, 2000);
  }, [playStarSound, playStreakSound, playCorrectSound]);

  const startLevel = useCallback(() => {
    setSessionStartTime(Date.now()); 
    setSessionMistakes([]); 
    setGamePhase('playing');
    resetLevelState();
  }, [resetLevelState]);

  const goToMenu = useCallback(() => {
    setGamePhase('menu');
  }, []);

  const handleNext = () => {
    playWhooshSound();
    
    const newIndex = wordIndex + wordsPerPage;
    
    if (newIndex >= shuffledWords.length) {
      if (allMatches.length < currentLevelWords.length) {
        alert('Complete all matches first before finishing the level!');
        return;
      }
      handleLevelComplete();
    } else {
      setWordIndex(newIndex);
      updateCurrentSets(shuffledWords, newIndex);
    }
  };

  const handlePrevious = () => {
    playWhooshSound();
    const newIndex = Math.max(0, wordIndex - wordsPerPage);
    setWordIndex(newIndex);
    updateCurrentSets(shuffledWords, newIndex);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    const draggedId = active.id;
    const droppedId = over.id;

    const draggedPair = currentLevelWords.find(
      p => `${p.english}-${p.emoji}` === draggedId
    );

    const correct = `${draggedPair.english}-${draggedPair.emoji}` === droppedId;
    setAttempts(prev => prev + 1);

    if (correct && !currentSetMatches.includes(draggedId)) {
      const newConsecutive = consecutiveCorrect + 1;
      setConsecutiveCorrect(newConsecutive);
      setCorrectAttempts(prev => prev + 1);
      
      let points = BASE_POINTS;
      if (newConsecutive >= 3) {
        points *= STREAK_MULTIPLIER;
        setStreakBonus(prev => prev + (BASE_POINTS * (STREAK_MULTIPLIER - 1)));
      }
      
      setAllMatches(prev => [...prev, draggedId]);
      setCurrentSetMatches(prev => [...prev, draggedId]);
      setTotalScore(prev => prev + points);
      setCurrentAttemptScore(prev => prev + points);
      setFeedback({ [draggedId]: 'correct' });
      setMatchedTargets(prev => ({ ...prev, [droppedId]: true }));
      
      const isStreak = newConsecutive >= 3;
      triggerCelebration(draggedId, isStreak);
    } else {
      setSessionMistakes(prev => [...prev, {
        timestamp: Date.now(),
        type: 'wrong_word_match',
        attempted: {
          arabic: draggedPair.arabic,
          english: draggedPair.english,
          droppedOn: droppedId
        },
        correctTarget: `${draggedPair.english}-${draggedPair.emoji}`,
        wordIndex: wordIndex,
        setIndex: Math.floor(wordIndex / wordsPerPage)
      }]);
      setConsecutiveCorrect(0);
      setFeedback({ [draggedId]: 'incorrect' });
      playIncorrectSound();
    }

    setTimeout(() => {
      setFeedback({});
    }, 1500);
  };

  const handleLevelComplete = useCallback(() => {
    if (allMatches.length < currentLevelWords.length) {
      alert('Complete all matches first!');
      return;
    }

    const finalTimeBonus = calculateTimeBonus(levelStartTime, currentLevelWords.length);
    const accuracy = Math.round((correctAttempts / attempts) * 100);
    const finalAccuracyBonus = calculateAccuracyBonus(correctAttempts, attempts);
    const finalTotalScore = totalScore + finalTimeBonus + finalAccuracyBonus;
    const earnedStars = calculateStars(finalTotalScore, currentLevelWords.length, accuracy);

    setTimeBonus(finalTimeBonus);
    setAccuracyBonus(finalAccuracyBonus);
    setStars(earnedStars);
    setCurrentAttemptScore(finalTotalScore);

    const scoreData = {
      totalScore: finalTotalScore,
      stars: earnedStars,
      accuracy: accuracy,
      timeBonus: finalTimeBonus,
      streakBonus: streakBonus,
      attempts: attempts
    };

    saveLevelData(level, scoreData);
    const sessionData = {
      gameType: 'wordMatchGame',
      level,
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000),
      totalScore: finalTotalScore,
      accuracy,
      attempts,
      correctAnswers: correctAttempts,
      mistakes: sessionMistakes,
      starsEarned: earnedStars,
      timeBonus: finalTimeBonus,
      streakBonus,
      accuracyBonus: finalAccuracyBonus
    };
    saveSessionData(sessionData);

    const nextLevel = level + 1;
    const isLastLevel = !levelsData[nextLevel.toString()];
    const passingScore = 70; // Default passing score
    const passedLevel = accuracy >= passingScore;
    
    setLevelCompleteData({
      ...scoreData,
      currentLevel: level,
      nextLevel: nextLevel,
      isLastLevel: isLastLevel,
      passedLevel: passedLevel,
      levelTitle: levelsData[level.toString()]?.title || `Level ${level}`,
      passingScore: passingScore
    });

    if (earnedStars > 0) {
      triggerCelebration(`level-complete-${level}`, false, earnedStars);
    }

    playPerfectSound();
    setGamePhase('complete');
    setShowLevelComplete(true);
    
    // Only unlock next level if passed and not last level
    if (passedLevel && !isLastLevel) {
      unlockNextLevel();
    }
  }, [
    allMatches.length,
    currentLevelWords.length,
    calculateTimeBonus,
    levelStartTime,
    correctAttempts,
    attempts,
    calculateAccuracyBonus,
    totalScore,
    calculateStars,
    streakBonus,
    level,
    levelsData,
    saveLevelData,
    triggerCelebration,
    playPerfectSound,
    unlockNextLevel
  ]);

  const handleProceedToNextLevel = useCallback(() => {
    if (!levelCompleteData) return;
    
    if (levelCompleteData.isLastLevel) {
      confetti({
        particleCount: 300,
        spread: 160,
        origin: { y: 0.4 },
        colors: ['#FFD700', '#FFA500', '#FF69B4', '#00CED1', '#32CD32'],
        shapes: ['star'],
        gravity: 0.6,
        drift: 0.2
      });
      alert("🎉 You've completed all levels! Amazing job!");
    } else if (levelCompleteData.passedLevel) {
      // Fixed: Only call updateLevel, not unlockNextLevel (already called in handleLevelComplete)
      updateLevel(levelCompleteData.nextLevel);
    }
    
    setShowLevelComplete(false);
    setLevelCompleteData(null);
    setGamePhase('menu');
  }, [levelCompleteData, updateLevel]);

  const handleResetProgress = useCallback(() => {
    setShowResetConfirmation(true);
  }, []);

  const confirmResetProgress = useCallback(() => {
    if (resetAllProgress) {
      resetAllProgress();
      resetLevelState();
      updateLevel(1);
      setShowResetConfirmation(false);
      setGamePhase('menu');
      playWhooshSound();
    }
  }, [resetAllProgress, resetLevelState, updateLevel, playWhooshSound]);

  const selectLevel = useCallback((newLevel) => {
    setLevel(newLevel);
  }, [setLevel]);

  const StarDisplay = ({ count, size = "text-2xl" }) => {
    return (
      <div className={`flex justify-center gap-1 ${size}`}>
        {[1, 2, 3].map(star => (
          <span 
            key={star} 
            className={`${star <= count ? 'text-yellow-400' : 'text-gray-300'} drop-shadow-lg transition-all duration-300 ${star <= count ? 'animate-pulse' : ''}`}
          >
            ⭐
          </span>
        ))}
      </div>
    );
  };

  const ScoreBreakdown = () => {
    const accuracy = attempts > 0 ? Math.round((correctAttempts / attempts) * 100) : 0;
    const currentBest = bestScores[`level${level}`];
    
    return (
      <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-6 border-4 border-blue-300">
        <h3 className="text-lg md:text-2xl font-bold text-center mb-2 md:mb-4 text-purple-600">📊 Your Score</h3>
        <button
          onClick={() => setShowScoringHelp(true)}
          className="w-full text-purple-500 hover:text-purple-700 text-sm font-medium underline mb-3"
          title="How does scoring work?"
        >
          ❓ How it works
        </button>
        
        {currentBest && (
          <div className="mb-4 p-3 md:p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border-2 border-blue-200">
            <h4 className="text-sm md:text-base font-bold text-center text-gray-700 mb-2">🎯 Your Best Score</h4>
            <div className="flex justify-between items-center text-xs md:text-sm">
              <div className="text-center">
                <p className="text-blue-600 font-bold text-lg">{currentAttemptScore}</p>
                <p className="text-gray-600">Current</p>
              </div>
              <div className="text-2xl">
                {currentAttemptScore > (currentBest.bestScore || currentBest.totalScore || 0) ? '🔥' : 
                 currentAttemptScore === (currentBest.bestScore || currentBest.totalScore || 0) ? '🎯' : '⬆️'}
              </div>
              <div className="text-center">
                <p className="text-green-600 font-bold text-lg">{currentBest.bestScore || currentBest.totalScore || 0}</p>
                <p className="text-gray-600">Best</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-3 md:gap-4 text-sm md:text-base mb-4">
          <div className="text-center bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-3 border-2 border-blue-300">
            <p className="text-blue-600 font-bold text-lg md:text-xl">{totalScore}</p>
            <p className="text-blue-700 font-semibold">Base Points</p>
          </div>
          <div className="text-center bg-gradient-to-br from-green-100 to-green-200 rounded-xl p-3 border-2 border-green-300">
            <p className="text-green-600 font-bold text-lg md:text-xl">{streakBonus}</p>
            <p className="text-green-700 font-semibold">Streak Bonus</p>
          </div>
          <div className="text-center bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl p-3 border-2 border-orange-300">
            <p className="text-orange-600 font-bold text-lg md:text-xl">{timeBonus}</p>
            <p className="text-orange-700 font-semibold">Time Bonus</p>
          </div>
          <div className="text-center bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl p-3 border-2 border-purple-300">
            <p className="text-purple-600 font-bold text-lg md:text-xl">{accuracyBonus}</p>
            <p className="text-purple-700 font-semibold">Accuracy Bonus</p>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-2xl p-4 border-2 border-indigo-300">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xl md:text-2xl font-black text-indigo-600">
                {totalScore + timeBonus + streakBonus + accuracyBonus}
              </p>
              <p className="text-sm md:text-base text-indigo-700 font-bold">Total Score</p>
            </div>
            <div className="text-center">
              <StarDisplay count={stars} size="text-xl md:text-2xl" />
              <p className="text-sm md:text-base text-indigo-700 font-bold">Stars Earned</p>
            </div>
          </div>
        </div>
        
        <div className="mt-3 text-center text-sm md:text-base text-gray-600 font-semibold">
          Accuracy: {accuracy}% ({correctAttempts}/{attempts})
        </div>
        
        {consecutiveCorrect >= 3 && (
          <div className="mt-3 text-center bg-gradient-to-r from-orange-100 to-red-100 rounded-xl p-3 border-2 border-orange-300">
            <p className="text-sm md:text-base text-orange-600 font-bold animate-pulse">
              🔥 {consecutiveCorrect} in a row! You're on fire! 🔥
            </p>
          </div>
        )}
      </div>
    );
  };

const DraggableWord = ({ pair }) => {
  const id = `${pair.english}-${pair.emoji}`;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  
  // Optimized transform style for faster dragging
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 1000 : 'auto',
    // Remove scale during drag to improve performance
    // scale: isDragging ? 1.05 : 1
  } : {};

  const feedbackClass = feedback[id] === 'correct'
    ? 'border-green-500 bg-green-100 animate-bounce shadow-2xl'
    : feedback[id] === 'incorrect'
      ? 'border-red-500 bg-red-100 animate-pulse shadow-2xl'
      : 'border-blue-400 hover:border-blue-500 hover:shadow-2xl';

  const playArabicAudio = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!pair.audio) return;
    
    setIsPlayingAudio(true);
    
    try {
      const audio = new Audio(pair.audio);
      audio.volume = 0.7;
      
      audio.addEventListener('ended', () => {
        setIsPlayingAudio(false);
      });
      
      audio.addEventListener('error', (e) => {
        console.warn('Audio failed to load:', pair.audio);
        setIsPlayingAudio(false);
      });
      
      await audio.play();
      
    } catch (error) {
      console.warn('Audio playback failed:', error.name);
      setIsPlayingAudio(false);
      
      if (error.name === 'NotAllowedError') {
        console.info('Audio autoplay blocked - user interaction required');
      }
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      {...listeners} 
      {...attributes} 
      style={{
        ...style,
        // Add performance hints for the browser
        willChange: isDragging ? 'transform' : 'auto',
        // Disable pointer events on children during drag to improve performance
        pointerEvents: isDragging ? 'none' : 'auto',
      }}
      className={`cursor-pointer w-32 h-24 md:w-40 md:h-28 flex flex-col items-center justify-center px-3 py-2 bg-gradient-to-br from-yellow-200 to-orange-200 rounded-2xl shadow-xl border-4 text-lg md:text-xl font-bold text-center transform hover:scale-105 ${feedbackClass} relative ${
        // Conditional transitions - only when NOT dragging
        isDragging 
          ? '' // No transitions during drag for instant response
          : 'transition-all duration-300' // Normal transitions when not dragging
      }`}
    >
      {/* Audio Button */}
      {pair.audio && (
        <button
          onClick={playArabicAudio}
          className={`absolute top-1 right-1 w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
            isPlayingAudio 
              ? 'bg-green-500 text-white animate-pulse' 
              : 'bg-white/80 text-gray-600 hover:bg-white hover:text-gray-800 hover:scale-110'
          } shadow-lg border border-gray-300 cursor-pointer z-10`}
          style={{ 
            pointerEvents: 'auto',
            // Keep audio button interactive during drag
            transform: isDragging ? 'scale(0.9)' : 'scale(1)',
          }}
          disabled={isPlayingAudio}
          title="Listen to pronunciation"
        >
          {isPlayingAudio ? (
            <div className="w-2 h-2 md:w-3 md:h-3 bg-white rounded-full animate-pulse"></div>
          ) : (
            <svg 
              className="w-3 h-3 md:w-4 md:h-4" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.824L4.632 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.632l3.751-3.824a1 1 0 011.617.824zM14.5 7.5a1 1 0 011.414 0 5 5 0 010 7.071 1 1 0 11-1.414-1.414 3 3 0 000-4.243 1 1 0 010-1.414z" clipRule="evenodd" />
              <path fillRule="evenodd" d="M16.5 5.5a1 1 0 011.414 0 7 7 0 010 9.899 1 1 0 11-1.414-1.414 5 5 0 000-7.071 1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          )}
        </button>
      )}
      
      {/* Arabic Text */}
      <div 
        className="text-gray-800 flex-1 flex items-center" 
        dir="rtl" 
        lang="ar"
        style={{
          // Optimize text rendering during drag
          pointerEvents: isDragging ? 'none' : 'auto',
        }}
      >
        {pair.arabic}
      </div>
    </div>
  );
};  

  const DroppableTarget = ({ pair }) => {
    const id = `${pair.english}-${pair.emoji}`;
    const { setNodeRef, isOver } = useDroppable({ id });
    const isMatched = matchedTargets[id];

    return (
      <div
        ref={setNodeRef}
        className={`w-32 h-24 md:w-40 md:h-28 flex items-center justify-center p-3 border-4 rounded-2xl shadow-xl text-lg md:text-xl font-bold text-center transition-all duration-300 ${
          isOver ? 'bg-gradient-to-br from-blue-300 to-blue-400 border-blue-500 scale-105 shadow-2xl' : 
          isMatched ? 'bg-gradient-to-br from-green-300 to-green-400 border-green-500 shadow-2xl' :
          'bg-gradient-to-br from-white to-gray-50 border-gray-400 hover:border-gray-500 hover:shadow-2xl'
        }`}
      >
        <div className="text-gray-800">{pair.english} {pair.emoji}</div>
      </div>
    );
  };

  // Effects
  useEffect(() => {
    if (userId) {
      setTimeout(startBackgroundMusic, 1000);
    }
  }, [userId, startBackgroundMusic]);

  useEffect(() => {
    if (level > 0 && levelsData[level.toString()] && !isLoading) {
      const pairs = levelsData[level.toString()].wordPairs || [];
      setCurrentLevelWords(pairs);
      const shuffled = shuffleArray([...pairs]);
      setShuffledWords(shuffled);
      resetLevelState();
      updateCurrentSets(shuffled, 0);
    }
  }, [level, levelsData, isLoading, shuffleArray, updateCurrentSets, resetLevelState]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full border-4 border-yellow-300">
          <div className="text-center">
            <div className="text-4xl md:text-6xl mb-4 animate-spin">🎓</div>
            <div className="text-purple-600 text-xl md:text-3xl font-bold animate-pulse">Loading Match & Learn...</div>
            <div className="text-gray-600 text-sm md:text-lg mt-2">Getting ready for learning!</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 p-4">
      <AudioElements />
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-4 drop-shadow-2xl">
            🎓 Match & Learn 🎓
          </h1>
          <p className="text-lg md:text-2xl text-white font-bold opacity-90 drop-shadow-lg">
            Match Arabic words with their English meanings!
          </p>
        </div>

        {gamePhase === 'menu' && (
          <div className="max-w-4xl mx-auto">
            {/* Level Info */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-4 md:mb-8 border-4 border-yellow-300">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-purple-600">
                🎮 Level {level}: {levelsData[level?.toString()]?.title || `Level ${level}`}
              </h2>
              <p className="text-center text-gray-700 mb-4 md:mb-6 text-lg md:text-xl font-semibold">
                Match Arabic words with their English meanings!
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-4 md:mb-8">
                <div className="text-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎯</div>
                  <p className="font-bold text-sm md:text-lg">Words</p>
                  <p className="text-lg md:text-2xl font-black">{currentLevelWords.length}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-green-400 to-green-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎮</div>
                  <p className="font-bold text-sm md:text-lg">Game Type</p>
                  <p className="text-lg md:text-2xl font-black">Match</p>
                </div>
                <div className="text-center bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🏆</div>
                  <p className="font-bold text-sm md:text-lg">Pass</p>
                  <p className="text-lg md:text-2xl font-black">70%</p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={startLevel}
                  className="px-8 md:px-12 py-4 md:py-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl font-black text-xl md:text-3xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white"
                >
                  🚀 START MATCHING! 🚀
                </button>
              </div>
            </div>

            {/* Level Grid */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 border-4 border-pink-300">
              <h3 className="text-xl md:text-3xl font-bold text-center mb-4 md:mb-6 text-purple-600">
                🎮 Choose Your Level!
              </h3>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2 md:gap-4">
                {Array.from({ length: Object.keys(levelsData).length || maxLevel }, (_, i) => i + 1).map(levelNum => {
                  const isUnlocked = levelNum <= maxLevel;
                  const isCurrent = levelNum === level;
                  const levelScore = bestScores[`level${levelNum}`];
                  
                  return (
                    <button
                      key={levelNum}
                      onClick={() => isUnlocked ? selectLevel(levelNum) : null}
                      disabled={!isUnlocked}
                      className={`
                        relative p-2 md:p-4 rounded-2xl text-sm md:text-lg font-bold transition-all duration-300 min-h-[80px] md:min-h-[120px] flex flex-col items-center justify-center border-2 md:border-4
                        ${isCurrent 
                          ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-2xl transform scale-105 md:scale-110 border-yellow-300' 
                          : isUnlocked 
                            ? 'bg-gradient-to-br from-green-100 to-green-200 text-green-800 hover:from-green-200 hover:to-green-300 cursor-pointer hover:scale-105 border-green-300' 
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-300'
                        }
                      `}
                      title={isUnlocked ? `Level ${levelNum}${levelScore ? ` (Best: ${levelScore.bestScore || levelScore.totalScore || 0})` : ''}` : 'Locked'}
                    >
                      <div className="text-lg md:text-2xl font-black">{levelNum}</div>
                      <div className="text-xs mt-1 font-bold hidden md:block">Match</div>
                      <div className="text-xs font-bold md:hidden">Match</div>
                      {levelScore && (
                        <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                          <StarDisplay count={levelScore.stars || 0} size="text-xs md:text-sm" />
                        </div>
                      )}
                      {!isUnlocked && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xl md:text-3xl">🔒</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-4 md:mt-8 flex gap-2 md:gap-4 justify-center flex-wrap">
              <Link to="/">
                <button 
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔙 Main Menu
                </button>
              </Link>
                <button 
                  onClick={handleResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🗑️ Reset Progress
                </button>
                <button 
                  onClick={() => setShowInstructions(true)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  📖 How to Play
                </button>
              </div>
            </div>
          </div>
        )}

        {gamePhase === 'playing' && (
          <div className="max-w-4xl mx-auto">
            {/* Game Stats */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 mb-6 shadow-xl">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🎯</div>
                  <p className="font-bold text-lg md:text-xl">{totalScore}</p>
                  <p className="text-xs font-bold">Score</p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">✅</div>
                  <p className="font-bold text-lg md:text-xl">{allMatches.length}/{currentLevelWords.length}</p>
                  <p className="text-xs font-bold">Matches</p>
                </div>
                <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🔄</div>
                  <p className="font-bold text-lg md:text-xl">{attempts}</p>
                  <p className="text-xs font-bold">Attempts</p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">📊</div>
                  <p className="font-bold text-lg md:text-xl">{attempts > 0 ? Math.round((correctAttempts / attempts) * 100) : 100}%</p>
                  <p className="text-xs font-bold">Accuracy</p>
                </div>
              </div>
              {consecutiveCorrect >= 3 && (
                <div className="mt-4 text-center bg-gradient-to-r from-orange-400/80 to-red-400/80 rounded-xl p-3">
                  <p className="text-white font-bold text-lg animate-pulse">
                    🔥 {consecutiveCorrect} in a row! You're on fire! 🔥
                  </p>
                </div>
              )}
            </div>

            {/* Game Board */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-6 border-4 border-yellow-300">
              <div className="text-center mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                  🎯 Match Words with Meanings
                </h2>
                <p className="text-gray-600 text-lg">Drag Arabic words to their English meanings!</p>
              </div>

              <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
                {/* Arabic Words (Draggables) */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-center mb-4 text-purple-600">📝 Arabic Words</h3>
                  <div className="flex flex-wrap justify-center gap-4 min-h-[120px] p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border-2 border-purple-200">
                    {currentDraggables.map((pair, idx) => {
                      const id = `${pair.english}-${pair.emoji}`;
                      return currentSetMatches.includes(id) ? (
                        <div key={`matched-drag-${idx}`} className="w-32 h-24 md:w-40 md:h-28 flex items-center justify-center text-green-500 text-4xl md:text-6xl border-4 border-green-400 bg-green-100 rounded-2xl shadow-xl animate-pulse">
                          ✓
                        </div>
                      ) : (
                        <DraggableWord key={`draggable-${idx}`} pair={pair} />
                      );
                    })}
                  </div>
                </div>

                {/* English Meanings (Drop Targets) */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-center mb-4 text-blue-600">🎯 English Meanings</h3>
                  <div className="flex flex-wrap justify-center gap-4 min-h-[120px] p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border-2 border-blue-200">
                    {currentTargets.map((pair, idx) => {
                      const id = `${pair.english}-${pair.emoji}`;
                      return matchedTargets[id] ? (
                        <div key={`matched-target-${idx}`} className="w-32 h-24 md:w-40 md:h-28 flex items-center justify-center text-green-500 text-4xl md:text-6xl border-4 border-green-400 bg-green-100 rounded-2xl shadow-xl animate-pulse">
                          ✓
                        </div>
                      ) : (
                        <DroppableTarget key={`droppable-${idx}`} pair={pair} />
                      );
                    })}
                  </div>
                </div>
              </DndContext>

              {/* Navigation Controls */}
              <div className="flex justify-center gap-4 md:gap-6">
                <button
                  onClick={handlePrevious}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={wordIndex === 0}
                >
                  <Target size={20} />
                  Previous
                </button>
                <button
                  onClick={handleNext}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 flex items-center gap-2"
                  disabled={currentSetMatches.length < Math.min(wordsPerPage, currentDraggables.length)}
                >
                  <Shuffle size={20} />
                  {wordIndex + wordsPerPage >= shuffledWords.length ? 'Complete Level!' : 'Next Set'}
                </button>
              </div>
            </div>

            {/* Controls */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-xl">
              <div className="flex gap-3 md:gap-4 justify-center flex-wrap">
                <button 
                  onClick={goToMenu}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Home size={20} />
                  Menu
                </button>
                <button 
                  onClick={startLevel}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <RotateCcw size={20} />
                  Restart Level
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Celebration Effects */}
        {Object.values(celebrations).some(Boolean) && (
          <div className="fixed inset-0 pointer-events-none z-40">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl md:text-9xl animate-bounce">🎉</div>
            </div>
          </div>
        )}

        {Object.values(sparkles).some(Boolean) && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {[...Array(15)].map((_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute animate-ping text-yellow-400 text-2xl"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      
      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-purple-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-purple-600">📖 How to Play!</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-blue-300">
                  <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-lg md:text-xl">🎯 Your Mission</h3>
                  <p className="text-blue-700 text-sm md:text-lg">Match Arabic words with their English meanings by dragging and dropping!</p>
                </div>
                
                <div className="bg-gradient-to-r from-green-100 to-green-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-green-300">
                  <h3 className="font-bold text-green-800 mb-2 md:mb-3 text-lg md:text-xl">🎮 How to Play</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Drag Arabic words from the purple area</li>
                    <li>Drop them onto their English meanings in the blue area</li>
                    <li>Correct matches will turn green and stay matched</li>
                    <li>Complete all matches in a set to move to the next set</li>
                    <li>Finish all sets to complete the level!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-yellow-300">
                  <h3 className="font-bold text-yellow-800 mb-2 md:mb-3 text-lg md:text-xl">🏆 Scoring</h3>
                  <ul className="list-disc list-inside text-yellow-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Earn points for each correct match</li>
                    <li>Get 3+ correct in a row for streak bonuses</li>
                    <li>Complete levels quickly for time bonuses</li>
                    <li>High accuracy earns accuracy bonuses</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-purple-300">
                  <h3 className="font-bold text-purple-800 mb-2 md:mb-3 text-lg md:text-xl">💡 Tips</h3>
                  <ul className="list-disc list-inside text-purple-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Take your time to learn the words</li>
                    <li>Focus on accuracy over speed</li>
                    <li>Use the emoji clues to help you</li>
                    <li>Try to get streaks for bonus points</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 md:mt-8 text-center">
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Let's Start Matching! 🎓
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Level Complete Modal */}
      {showLevelComplete && levelCompleteData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-4 md:p-8 border-4 border-yellow-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">
                {levelCompleteData.passedLevel ? '🎉' : '😊'}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-purple-600 mb-2 md:mb-3">
                Level {levelCompleteData.currentLevel} Complete!
              </h2>
              <h3 className="text-lg md:text-xl text-gray-700 mb-4 md:mb-6 font-semibold">
                {levelCompleteData.levelTitle}
              </h3>
              
              <div className="mb-6 md:mb-8">
                <StarDisplay count={levelCompleteData.stars} size="text-3xl md:text-4xl" />
                <p className="text-xl md:text-2xl font-bold text-indigo-600 mt-3 md:mt-4">
                  Total Score: {levelCompleteData.totalScore}
                </p>
                <p className="text-base md:text-lg text-gray-600 mt-2">
                  Accuracy: {levelCompleteData.accuracy}%
                </p>
              </div>

              <div className="mb-6 md:mb-8">
                {levelCompleteData.passedLevel ? (
                  <div className="bg-gradient-to-r from-green-100 to-green-200 border-2 md:border-4 border-green-400 rounded-2xl p-4 md:p-6">
                    <p className="text-green-800 font-bold text-lg md:text-xl">
                      ✅ Fantastic! You passed! 🎉
                    </p>
                    <p className="text-green-700 text-base md:text-lg">
                      {levelCompleteData.accuracy}% ≥ {levelCompleteData.passingScore}%
                    </p>
                    {!levelCompleteData.isLastLevel && (
                      <p className="text-green-700 text-base md:text-lg mt-2">Ready for level {levelCompleteData.nextLevel}?</p>
                    )}
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-orange-100 to-orange-200 border-2 md:border-4 border-orange-400 rounded-2xl p-4 md:p-6">
                    <p className="text-orange-800 font-bold text-lg md:text-xl">
                      ☹️ Good try! Practice makes perfect!
                    </p>
                    <p className="text-orange-700 text-base md:text-lg">
                      You need {levelCompleteData.passingScore}% accuracy to pass
                    </p>
                    <p className="text-orange-700 text-base md:text-lg">Try again to unlock the next level!</p>
                  </div>
                )}
              </div>

              <div className="space-y-3 md:space-y-4">
                <button 
                  onClick={() => { 
                    setShowLevelComplete(false); 
                    setGamePhase('menu'); 
                  }}
                  className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔄 Play Again
                </button>
                
                {levelCompleteData.passedLevel && !levelCompleteData.isLastLevel && (
                  <button 
                    onClick={handleProceedToNextLevel}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🚀 Next Level! 🚀
                  </button>
                )}
                
                {levelCompleteData.isLastLevel && (
                  <button 
                    onClick={() => { 
                      setShowLevelComplete(false); 
                      setGamePhase('menu'); 
                    }}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🎊 You're a Word Master! 🎊
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scoring Help Modal */}
      {showScoringHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-purple-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-purple-600">🏆 How Scoring Works</h2>
                <button 
                  onClick={() => setShowScoringHelp(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-blue-300">
                  <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-lg md:text-xl">📊 Base Points</h3>
                  <p className="text-blue-700 text-sm md:text-lg">You earn <strong>{BASE_POINTS} points</strong> for each correct match.</p>
                </div>
                
                <div className="bg-gradient-to-r from-orange-100 to-orange-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-orange-300">
                  <h3 className="font-bold text-orange-800 mb-2 md:mb-3 text-lg md:text-xl">🔥 Streak Bonus</h3>
                  <p className="text-orange-700 text-sm md:text-lg">Get 3 or more correct matches in a row to earn <strong>double points</strong> for each match in the streak!</p>
                </div>
                
                <div className="bg-gradient-to-r from-green-100 to-green-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-green-300">
                  <h3 className="font-bold text-green-800 mb-2 md:mb-3 text-lg md:text-xl">⏱️ Time Bonus</h3>
                  <p className="text-green-700 text-sm md:text-lg">Complete the level faster to earn up to <strong>{MAX_TIME_BONUS} bonus points</strong>. The ideal time is 5 seconds per word.</p>
                </div>
                
                <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-purple-300">
                  <h3 className="font-bold text-purple-800 mb-2 md:mb-3 text-lg md:text-xl">🎯 Accuracy Bonus</h3>
                  <p className="text-purple-700 text-sm md:text-lg">Higher accuracy earns more bonus points:</p>
                  <ul className="list-disc list-inside mt-2 md:mt-3 text-purple-600 text-sm md:text-lg">
                    <li>100% accuracy: <strong>{PERFECT_ACCURACY_BONUS} points</strong></li>
                    <li>Less than perfect: Up to <strong>{MAX_ACCURACY_BONUS} points</strong> based on your accuracy percentage</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-yellow-300">
                  <h3 className="font-bold text-yellow-800 mb-2 md:mb-3 text-lg md:text-xl">⭐ Star Rating</h3>
                  <p className="text-yellow-700 text-sm md:text-lg">Stars are awarded based on your overall performance:</p>
                  <ul className="list-disc list-inside mt-2 md:mt-3 text-yellow-600 text-sm md:text-lg">
                    <li><span className="text-yellow-400">⭐⭐⭐</span> Perfect performance (100% accuracy + high score)</li>
                    <li><span className="text-yellow-400">⭐⭐</span> Great performance (85%+ accuracy + good score)</li>
                    <li><span className="text-yellow-400">⭐</span> Good performance (60%+ accuracy + decent score)</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 md:mt-8 text-center">
                <button 
                  onClick={() => setShowScoringHelp(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Got it! 👍
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-red-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">⚠️</div>
              <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-3 md:mb-4">Reset All Progress?</h2>
              <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-lg">
                This will delete all your scores, stars, and progress. You'll start from Level 1 again.
                This action cannot be undone!
              </p>
              <div className="flex gap-3 md:gap-4 justify-center">
                <button 
                  onClick={() => setShowResetConfirmation(false)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Yes, Reset Everything
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordMatchGameV2;